import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';

/**
 * Progress indicator for multi-step workflows
 * Includes a minimal mode optimized for container header
 * 
 * @param {Object} props - Component props
 * @param {number} props.currentStep - Current active step (0-based index)
 * @param {Array<string>} props.steps - Array of step labels
 * @param {boolean} [props.vertical=false] - Whether to display vertically
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onStepClick] - Optional callback when a step is clicked
 * @param {boolean} [props.minimal=false] - Whether to use minimal mode (just dots with current step label)
 */
const ProgressIndicator = ({ 
    currentStep, 
    steps, 
    vertical = false, 
    className = '',
    onStepClick,
    minimal = false
}) => {
    // For minimal mode, we'll use a simpler layout
    if (minimal) {
        return (
            <div className={`relative flex items-center ${className}`}>
                {/* Progress bar background */}
                <div className="absolute h-1 bg-white/20 rounded-full w-full"></div>
                
                {/* Progress bar fill */}
                <div 
                    className="absolute h-1 bg-yellow-300 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                ></div>
                
                {/* Step indicators */}
                <div className="relative w-full flex justify-between items-center py-3">
                    {steps.map((label, index) => {
                        // Determine the status of this step
                        const isCompleted = index < currentStep;
                        const isCurrent = index === currentStep;
                        const isClickable = onStepClick && (isCompleted || index === currentStep);
                        
                        return (
                            <div 
                                key={index}
                                className={`flex flex-col items-center ${isClickable ? 'cursor-pointer' : ''}`}
                                onClick={isClickable ? () => onStepClick(index) : undefined}
                                title={label}
                            >
                                {/* Step dot */}
                                <div 
                                    className={`w-3 h-3 rounded-full ${
                                        isCurrent ? 'bg-yellow-300 ring-2 ring-yellow-300/50 ring-offset-1 ring-offset-green-800' : 
                                        isCompleted ? 'bg-yellow-300' : 'bg-white/30'
                                    } transition-all duration-200`}
                                ></div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Default to horizontal layout for original mode
    const containerClasses = vertical
        ? 'flex flex-col space-y-4'
        : 'flex items-center space-x-1 md:space-x-2 justify-between';

    // On tablet, we want a more compact display
    const isTablet = typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth <= 1024;

    return (
        <div className={`${containerClasses} ${className}`}>
            {steps.map((label, index) => {
                // Determine the status of this step
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;
                const isClickable = onStepClick && (isCompleted || index === currentStep);
                
                // Base classes for the step indicator
                const stepClasses = `
                    flex ${vertical ? 'items-start' : 'flex-col md:flex-row items-center'} 
                    ${vertical ? 'space-x-3' : 'md:space-x-2 space-y-1 md:space-y-0'} 
                    ${isCurrent ? 'text-yellow-300' : isCompleted ? 'text-white' : 'text-white/50'}
                    ${isClickable ? 'cursor-pointer hover:opacity-80' : ''}
                    ${index === currentStep ? 'flex-1 md:flex-none' : 'flex-none'}
                `;
                
                // Classes for connecting lines
                const lineClasses = `
                    ${vertical ? 'w-0.5 h-4 ml-4 -mt-1 mb-1' : 'h-0.5 w-full max-w-[30px] hidden md:block'}
                    ${index < steps.length - 1 ? '' : 'hidden'} 
                    ${isCompleted ? 'bg-yellow-300' : 'bg-white/20'}
                `;

                const handleClick = () => {
                    if (isClickable && onStepClick) {
                        onStepClick(index);
                    }
                };

                return (
                    <React.Fragment key={index}>
                        <div 
                            className={stepClasses}
                            onClick={handleClick}
                        >
                            <div 
                                className={`
                                    ${vertical ? 'mt-0' : ''}
                                    transition-all duration-300
                                    flex items-center justify-center
                                    ${isCurrent ? 'transform scale-110' : ''}
                                `}
                            >
                                {isCompleted ? (
                                    <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-yellow-300 fill-green-800/50" />
                                ) : isCurrent ? (
                                    <div className="relative">
                                        <Circle className="w-5 h-5 md:w-6 md:h-6 text-yellow-300 fill-green-800/30 stroke-2" />
                                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                                            <div className="w-1.5 h-1.5 rounded-full bg-yellow-300"></div>
                                        </div>
                                    </div>
                                ) : (
                                    <Circle className="w-5 h-5 md:w-6 md:h-6 text-white/50 stroke-1" />
                                )}
                            </div>
                            <span 
                                className={`
                                    text-xs md:text-sm font-medium whitespace-nowrap
                                    ${vertical ? '' : isCurrent ? '' : 'hidden md:block'}
                                    ${isCurrent ? 'font-semibold' : ''}
                                `}
                            >
                                {label}
                            </span>
                        </div>
                        
                        {/* Connecting line between steps */}
                        {index < steps.length - 1 && (
                            <div className={lineClasses}></div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ProgressIndicator; 